import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../lib/auth";
import { ArrowLeft, Save, Send } from "lucide-react";

const PROVIDERS = [
  { key: "none", label: "None (pending integration)" },
  { key: "resend", label: "Resend" },
  { key: "sendgrid", label: "SendGrid" },
  { key: "mailgun", label: "Mailgun" },
  { key: "smtp", label: "SMTP" },
];

export default function EmailProviderPage() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [cfg, setCfg] = useState(null);
  const [testTo, setTestTo] = useState("");
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!user?.is_super_admin) return;
    api.get("/admin/email-provider").then(r => setCfg(r.data)).catch(()=>{
      setCfg({ provider: "none", from_email: "", from_name: "MMS HUB CENTER", api_key: "" });
    });
  }, [user]);

  if (!user?.is_super_admin) {
    return <div className="p-8 text-red-600 text-sm">Only super admins can configure the email provider.</div>;
  }
  if (!cfg) return <div className="p-8 text-[#737373] text-xs">Loading…</div>;

  const save = async () => {
    setSaving(true); setErr(""); setMsg("");
    try {
      const payload = { ...cfg };
      // if the field is still the mask, don't send it — server will preserve
      if (payload.api_key === "•••••••") delete payload.api_key;
      if (payload.smtp_password === "•••••••") delete payload.smtp_password;
      await api.put("/admin/email-provider", payload);
      setMsg("Provider saved.");
      const r = await api.get("/admin/email-provider");
      setCfg(r.data);
    } catch (e) { setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setSaving(false); }
  };

  const test = async () => {
    if (!testTo) return;
    setErr(""); setMsg("");
    try {
      const r = await api.post("/admin/email-provider/test", { to: testTo });
      if (r.data.status === "sent") setMsg("Test email sent successfully.");
      else if (r.data.status === "pending") setMsg("Provider not configured — email marked as pending.");
      else setErr(`Test failed: ${r.data.error || r.data.status}`);
    } catch (e) { setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const showApiKey = ["resend", "sendgrid", "mailgun"].includes(cfg.provider);
  const showSmtp = cfg.provider === "smtp";
  const showMailgunDomain = cfg.provider === "mailgun";

  return (
    <div className="min-h-screen bg-[#FAFAFA] text-[#0A0A0A] p-8" data-testid="email-provider-page">
      <button onClick={()=>nav(-1)} className="text-[#525252] hover:text-[#0A0A0A] text-xs uppercase tracking-widest inline-flex items-center gap-2 mb-6" data-testid="email-back">
        <ArrowLeft size={12}/> Back
      </button>
      <div className="max-w-2xl mx-auto card-dark p-8 space-y-4">
        <div className="label-caps text-[#0A0A0A]">Platform Settings</div>
        <h1 className="font-serif text-3xl">Email Provider</h1>
        <p className="text-sm text-[#525252]">Configure the provider used to deliver all task, chat and system emails. If left as <em>None</em>, emails will be logged and marked &quot;pending integration&quot;.</p>

        <div>
          <label className="label-caps block mb-2">Provider</label>
          <select className="input-dark" value={cfg.provider} onChange={(e)=>setCfg({...cfg, provider: e.target.value})} data-testid="email-provider-select">
            {PROVIDERS.map(p => <option key={p.key} value={p.key}>{p.label}</option>)}
          </select>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="label-caps block mb-2">From email</label>
            <input className="input-dark" value={cfg.from_email || ""} onChange={(e)=>setCfg({...cfg, from_email: e.target.value})} placeholder="hello@yourdomain.com"/>
          </div>
          <div>
            <label className="label-caps block mb-2">From name</label>
            <input className="input-dark" value={cfg.from_name || ""} onChange={(e)=>setCfg({...cfg, from_name: e.target.value})}/>
          </div>
        </div>

        {showApiKey && (
          <div>
            <label className="label-caps block mb-2">API key</label>
            <input className="input-dark" placeholder="Leave blank to keep existing" value={cfg.api_key || ""} onChange={(e)=>setCfg({...cfg, api_key: e.target.value})} data-testid="email-api-key"/>
          </div>
        )}

        {showMailgunDomain && (
          <div>
            <label className="label-caps block mb-2">Mailgun domain</label>
            <input className="input-dark" placeholder="mg.yourdomain.com" value={cfg.smtp_host || ""} onChange={(e)=>setCfg({...cfg, smtp_host: e.target.value})}/>
          </div>
        )}

        {showSmtp && (
          <div className="grid grid-cols-2 gap-3">
            <div><label className="label-caps block mb-2">SMTP host</label>
              <input className="input-dark" value={cfg.smtp_host || ""} onChange={(e)=>setCfg({...cfg, smtp_host: e.target.value})}/></div>
            <div><label className="label-caps block mb-2">SMTP port</label>
              <input className="input-dark" type="number" value={cfg.smtp_port || 465} onChange={(e)=>setCfg({...cfg, smtp_port: parseInt(e.target.value,10)})}/></div>
            <div><label className="label-caps block mb-2">SMTP user</label>
              <input className="input-dark" value={cfg.smtp_user || ""} onChange={(e)=>setCfg({...cfg, smtp_user: e.target.value})}/></div>
            <div><label className="label-caps block mb-2">SMTP password</label>
              <input className="input-dark" type="password" placeholder="Leave blank to keep existing" value={cfg.smtp_password || ""} onChange={(e)=>setCfg({...cfg, smtp_password: e.target.value})}/></div>
          </div>
        )}

        {msg && <div className="text-emerald-400 text-xs">{msg}</div>}
        {err && <div className="text-red-600 text-xs">{err}</div>}

        <div className="flex items-center justify-between border-t border-[#E5E5E5] pt-4">
          <button onClick={save} disabled={saving} className="btn-gold inline-flex items-center gap-2" data-testid="email-save">
            <Save size={14}/> {saving ? "Saving…" : "Save"}
          </button>
          <div className="flex items-center gap-2">
            <input placeholder="test@example.com" className="input-dark py-1 text-xs w-56" value={testTo} onChange={(e)=>setTestTo(e.target.value)} data-testid="email-test-to"/>
            <button onClick={test} disabled={!testTo} className="btn-ghost inline-flex items-center gap-2" data-testid="email-test-btn">
              <Send size={12}/> Send test
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
